import type { CSSProperties, ReactNode } from 'react'
import { useMemo } from 'react'

const TOKEN_NAME = /^--[a-zA-Z0-9_-]+$/

/**
 * Branding tokens arrive as data from `tenant.branding.tokens` and are written
 * onto one wrapper element as CSS custom properties. Every colour and every
 * family in the customer room reads `var(--fr-*)` off this element, so a
 * different boutique is a different row in the database and not a rebuild.
 *
 * The names are validated and the values are stripped of anything that could
 * escape a declaration — the tokens are operator input, and operator input
 * that reaches a stylesheet is still untrusted input.
 */
export function BrandingScope({
  tokens,
  className,
  children,
}: {
  readonly tokens: Readonly<Record<string, string>>
  readonly className?: string
  readonly children: ReactNode
}) {
  const style = useMemo<CSSProperties>(() => {
    const safe: Record<string, string> = {}
    for (const [name, value] of Object.entries(tokens)) {
      if (!TOKEN_NAME.test(name)) continue
      if (typeof value !== 'string') continue
      if (/[;{}<>]|url\s*\(|expression\s*\(|@import/i.test(value)) continue
      safe[name] = value.trim().slice(0, 200)
    }
    return safe as CSSProperties
  }, [tokens])

  return (
    <div className={className} style={style}>
      {children}
    </div>
  )
}
